import React, { useEffect, useState } from 'react';

import { faBan, faBuilding, faCheck, faRotate, faUpload } from '@fortawesome/pro-solid-svg-icons';
import { Card } from '@buildoutinc/blueprint-react/ui/Card';
import { Field } from '@buildoutinc/blueprint-react/ui/Field';
import { Input } from '@buildoutinc/blueprint-react/ui/Input';

import { get, patch, post } from 'helpers/api';
import { requestErrors } from 'helpers/api_errors';
import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { Errors } from 'components/ui/errors';
import LoadingSpinner from 'components/ui/loading_spinner';
import PageHeader from 'components/ui/page_header';

import EmployersTable from './employers_table';
import StatusBadge from './status_badge';
import type { PrincipalEmployer, ReportData } from './types';

interface Props {
  paths: { report: string; reports: string };
}

export default function AcfrReportShow({ paths }: Props) {
  const [data, setData] = useState<ReportData | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;

    async function load(): Promise<void> {
      try {
        const result: ReportData = await get(paths.report);
        if (!cancelled) setData(result);
      } catch (error) {
        if (!cancelled) setErrors(requestErrors(error));
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [paths.report]);

  async function run(request: () => Promise<ReportData>): Promise<void> {
    setBusy(true);
    try {
      setData(await request());
      setErrors([]);
    } catch (error) {
      setErrors(requestErrors(error));
    } finally {
      setBusy(false);
    }
  }

  if (!data) {
    return errors.length ? <Errors className="m-4" errors={errors} /> : <LoadingSpinner />;
  }

  const { report, employers } = data;
  const title = [report.countyName || 'Unknown county', report.state].join(', ');

  function updateEmployers(update: (employers: PrincipalEmployer[]) => PrincipalEmployer[]): void {
    setData(current => current && { ...current, employers: update(current.employers) });
  }

  return (
    <>
      <PageHeader>
        <PageHeader.Heading
          breadcrumbs={[{ label: 'ACFR reports', href: paths.reports }]}
          icon={faBuilding}
          title={`${title}${report.fiscalYear ? ` FY${report.fiscalYear}` : ''}`}
        />
        <PageHeader.Actions>
          <StatusBadge status={report.status} />
          <Button
            disabled={busy || !report.pdfUrl || report.status === 'no_employer_table'}
            icon={faRotate}
            label="Re-run extraction"
            variant="outline"
            onClick={() => run(() => post(`${paths.report}/extract`))}
          />
          <Button
            disabled={busy || report.status === 'no_employer_table'}
            icon={faBan}
            label="No employer table"
            tooltip="This ACFR reports employment by industry only. It won't be searched for or extracted again."
            variant="outline"
            onClick={() => run(() => post(`${paths.report}/no_employer_table`))}
          />
          <Button
            disabled={busy || report.status === 'reviewed'}
            icon={faCheck}
            label="Mark reviewed"
            tooltip="Makes these employers available to documents."
            onClick={() => run(() => post(`${paths.report}/review`))}
          />
        </PageHeader.Actions>
      </PageHeader>

      <div className="container-fluid py-4">
        <div className="row g-4">
          <div className="col-lg-9 d-flex flex-column gap-3">
            <Errors errors={errors} />
            {report.reviewReasons.length > 0 && report.status === 'needs_review' && (
              <Alert severity="warning">
                <Alert.Title>Needs review</Alert.Title>
                {report.reviewReasons.join(', ')}
              </Alert>
            )}
            {report.status === 'fetched' && (
              <Alert severity="info">Extraction is running. Reload in a minute to see the employers.</Alert>
            )}
            {employers.length > 0 || report.fiscalYear ? (
              <EmployersTable
                employers={employers}
                employersPath={`${paths.report}/principal_employers`}
                fiscalYear={report.fiscalYear}
                onChange={updateEmployers}
              />
            ) : (
              <p className="text-muted">No employers yet. Set the fiscal year to add them by hand.</p>
            )}
          </div>

          <div className="col-lg-3 d-flex flex-column gap-3">
            <SourceCard data={data} />
            <DetailsForm busy={busy} data={data} key={report.updatedAt} reportPath={paths.report} onSave={run} />
          </div>
        </div>
      </div>
    </>
  );
}

interface SourceCardProps {
  data: ReportData;
}

function SourceCard({ data: { report } }: SourceCardProps) {
  return (
    <Card>
      <Card.Header>
        <Card.Title>Source</Card.Title>
      </Card.Header>
      <Card.Body className="d-flex flex-column gap-2">
        {report.issuerName && <div>{report.issuerName}</div>}
        {report.sourceUrl && (
          <a className="text-break" href={report.sourceUrl} rel="noopener noreferrer" target="_blank">
            {report.sourceUrl}
          </a>
        )}
        {report.pdfUrl ? (
          <div>
            <a href={report.pdfUrl} rel="noopener noreferrer" target="_blank">Open PDF</a>
            {report.schedulePages.length > 0 && (
              <div>
                Schedule on page
                {report.schedulePages.length > 1 && 's'}{' '}
                {report.schedulePages.map((page, index) => (
                  <React.Fragment key={page}>
                    {index > 0 && ', '}
                    <a href={`${report.pdfUrl}#page=${page}`} rel="noopener noreferrer" target="_blank">{page}</a>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="text-muted">No PDF yet. Upload one below.</div>
        )}
        {report.reviewedBy && (
          <div className="small text-muted">
            Reviewed by {report.reviewedBy}, {new Date(report.reviewedAt ?? '').toLocaleDateString()}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

interface DetailsFormProps {
  data: ReportData;
  reportPath: string;
  busy: boolean;
  onSave: (request: () => Promise<ReportData>) => Promise<void>;
}

// Keyed on the report's updatedAt by the caller, so it starts over from whatever the server last returned.
function DetailsForm({ data: { report }, reportPath, busy, onSave }: DetailsFormProps) {
  const [countyName, setCountyName] = useState<string>(report.countyName ?? '');
  const [fiscalYear, setFiscalYear] = useState<string>(report.fiscalYear?.toString() ?? '');
  const [file, setFile] = useState<File | null>(null);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();

    const body = new FormData();
    body.append('acfr_report[county_name]', countyName);
    body.append('acfr_report[fiscal_year]', fiscalYear);
    if (file) body.append('pdf', file);
    onSave(() => patch(reportPath, body));
  }

  return (
    <Card>
      <Card.Header>
        <Card.Title>Details</Card.Title>
      </Card.Header>
      <Card.Body>
        <form className="d-flex flex-column gap-3" onSubmit={handleSubmit}>
          <Field>
            <Field.Label>County name</Field.Label>
            <Input
              value={countyName}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCountyName(event.target.value)}
            />
          </Field>
          <Field>
            <Field.Label>Fiscal year</Field.Label>
            <Input
              type="number"
              value={fiscalYear}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFiscalYear(event.target.value)}
            />
          </Field>
          <Field>
            <Field.Label>Replace PDF</Field.Label>
            <Input
              accept="application/pdf"
              type="file"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFile(event.target.files?.[0] ?? null)}
            />
            <Field.Description>Uploading a PDF extracts its employers again.</Field.Description>
          </Field>
          <div>
            <Button
              disabled={busy}
              icon={file ? faUpload : null}
              label={file ? 'Save and extract' : 'Save'}
              type="submit"
            />
          </div>
        </form>
      </Card.Body>
    </Card>
  );
}
